import React from 'react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    Home, UserPlus, Phone, Calendar,
    FileText, CheckCircle2, DollarSign,
    ExternalLink, ChevronRight
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const ICONS = {
    'create_property': { icon: Home, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Propiedad Creada' },
    'edit_property': { icon: Home, color: 'text-amber-600', bg: 'bg-amber-100', label: 'Propiedad Editada' },
    'new_lead': { icon: UserPlus, color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Nuevo Lead' },
    'pipeline_change': { icon: ExternalLink, color: 'text-purple-600', bg: 'bg-purple-100', label: 'Etapa Cambiada' },
    'contract_gen': { icon: FileText, color: 'text-indigo-600', bg: 'bg-indigo-100', label: 'Contrato Generado' },
    'appointment_done': { icon: CheckCircle2, color: 'text-teal-600', bg: 'bg-teal-100', label: 'Cita Completada' },
    'task_done': { icon: CheckCircle2, color: 'text-orange-600', bg: 'bg-orange-100', label: 'Tarea Realizada' },
    'default': { icon: Calendar, color: 'text-slate-600', bg: 'bg-slate-100', label: 'Actividad' }
};

export const ActivityItem = ({ activity, isLast }) => {
    const config = ICONS[activity.type] || ICONS.default;
    const Icon = config.icon;

    // Simulate relative time if not provided as Date object
    const timeDisplay = activity.timestamp
        ? formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true, locale: es })
        : activity.time;

    return (
        <div className="relative flex gap-4 समूह group">
            {/* Timeline Line */}
            {!isLast && (
                <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-slate-100 group-hover:bg-blue-100 transition-colors" />
            )}

            {/* Icon Node */}
            <div className={cn(
                "relative z-10 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all group-hover:scale-110",
                config.bg
            )}>
                <Icon className={cn("w-4 h-4", config.color)} />
            </div>

            {/* Content Card */}
            <div className="flex-1 pb-6">
                <div className="bg-white group-hover:bg-slate-50/50 p-3 rounded-2xl border border-transparent group-hover:border-slate-100 transition-all">
                    <div className="flex justify-between items-start gap-2">
                        <div>
                            <p className="text-sm font-bold text-slate-800 leading-snug">
                                <span className="text-blue-600">@{activity.user || 'Sistema'}</span> {activity.text}
                            </p>
                            <div className="mt-1 flex items-center gap-2">
                                <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">
                                    {timeDisplay}
                                </span>
                                {activity.badge && (
                                    <Badge variant="outline" className="text-[9px] h-4 py-0 font-bold bg-white">
                                        {activity.badge}
                                    </Badge>
                                )}
                            </div>
                        </div>
                        {activity.link && (
                            <button className="text-slate-300 hover:text-blue-500 transition-colors">
                                <ChevronRight size={18} />
                            </button>
                        )}
                    </div>

                    {activity.details && (
                        <div className="mt-2 p-2 bg-slate-50 rounded-lg text-xs text-slate-500 font-medium italic">
                            "{activity.details}"
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
